import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismBadge,
  PrismCard,
  PrismIcon,
  PrismMetricBlock,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import topSearchingCountriesFormat from 'lumen/helpers/top-searching-countries-format';

interface HotelMarketInsightCardsSearchesSignature {
  Args: {
    detailSectionId: unknown;
    flightBusinessSearchSummaryForCurrentSelection: unknown;
    flightLeisureSearchSummaryForCurrentSelection: unknown;
    hotelBusinessSearchSummaryForCurrentSelection: unknown;
    hotelLeisureSearchSummaryForCurrentSelection: unknown;
    searchesTooltipText: unknown;
    setFocusedDetailSection: unknown;
  };
}

export default class HotelMarketInsightCardsSearches extends Component<HotelMarketInsightCardsSearchesSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare miSearches: Services['mi-searches'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasFlightBusinessSearches() {
    return this.miSearches
      ?.areFlightBusinessSearchesSupportedForTheCurrentDestination;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasFlightLeisureSearches() {
    return this.miSearches
      ?.areFlightLeisureSearchesSupportedForTheCurrentDestination;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasFlightSearches() {
    return (
      this.miSearches
        ?.areFlightLeisureSearchesSupportedForTheCurrentDestination ||
      this.miSearches
        ?.areFlightBusinessSearchesSupportedForTheCurrentDestination
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasHotelBusinessSearches() {
    return this.miSearches
      ?.areHotelBusinessSearchesSupportedForTheCurrentDestination;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasHotelLeisureSearches() {
    return this.miSearches
      ?.areHotelLeisureSearchesSupportedForTheCurrentDestination;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasHotelSearches() {
    return (
      this.miSearches
        ?.areHotelLeisureSearchesSupportedForTheCurrentDestination ||
      this.miSearches?.areHotelBusinessSearchesSupportedForTheCurrentDestination
    );
  }

  @action
  viewSearchesButtonClicked(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const day = this.args.selectedDayModel.date;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { outliers } = this.args.selectedDayModel;

    this.usageAnalytics.track('Last 7 Days Searches Viewed', {
      eventTrigger: 'day-detail-see-more',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      daysOffsetFromToday: day
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .startOf('day')
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .diff(this.hotelAndMarketService.getLocalTime().startOf('day'), 'days'),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      isOutlier: outliers.length > 0,
    });

    // @ts-expect-error: Incorrect type
    this.args.setFocusedDetailSection(this.args.detailSectionId);
  }

  <template>
    {{! @glint-nocheck }}
    <PrismCard
      class="detail-pane-card detail-pane-card--searches"
      data-test-id="market-insight-searches-card"
    >
      <:header as |Header|>
        <div class="flex items-center gap-100">
          <Header.title>
            {{t "market_insight.searches_in_the_last_days" count=7}}
          </Header.title>
          <GeneralIconWithTooltip
            @side="right"
            @tooltipContent={{@searchesTooltipText}}
          />
        </div>
      </:header>
      <:headerAfter>
        <button
          class="back-button button--unstyled flex flex-row items-center"
          data-test-id="searches-focused-detail-button"
          type="button"
          {{on "click" this.viewSearchesButtonClicked}}
        >
          <p class="text-200--regular mr-100">{{t "general.see_details"}}</p>
          <PrismIcon @name="arrow-right" @size="small" />
        </button>
      </:headerAfter>
      <:content>
        {{#if this.hasFlightLeisureSearches}}
          <div class="mt-100 mb-300">
            <div class="search-type-title">
              <PrismIcon @name="plane" />
              {{t "market_insight.searches.flights"}}
              {{#if this.hasFlightBusinessSearches}}
                ({{t "general.meta"}}/{{t "general.ota"}})
              {{else if this.hasHotelBusinessSearches}}
                ({{t "general.meta"}}/{{t "general.ota"}}/{{t "general.GDS"}})
              {{/if}}
            </div>
            <div class="flex flex-row">
              <PrismMetricBlock
                @align="left"
                @compact={{true}}
                class="detail-pane-metric-block--search-level"
              >
                <:value>
                  <PrismBadge
                    @badgeType={{@flightLeisureSearchSummaryForCurrentSelection.numSearchesAsBadgeClass}}
                    @label={{@flightLeisureSearchSummaryForCurrentSelection.numSearchesAsLabel}}
                  />
                </:value>
                <:label>
                  <div class="mt-100">{{t
                      "market_insight.search_patterns.search_level"
                    }}</div></:label>
              </PrismMetricBlock>
              <PrismMetricBlock
                @align="left"
                @compact={{true}}
                class="detail-pane-metric-block--most-searched-los"
              >
                <:value>
                  <span
                    class="text-400--bold"
                  >{{@flightLeisureSearchSummaryForCurrentSelection.formattedMostSearchedLos}}</span>
                </:value>
                <:label>
                  <div class="mt-100">{{t
                      "market_insight.search_patterns.most_searched_los"
                    }}</div>
                </:label>
              </PrismMetricBlock>
              <PrismMetricBlock
                @align="left"
                @compact={{true}}
                class="detail-pane-metric-block--top-countries"
              >
                <:value>
                  <span class="text-400--bold">
                    {{topSearchingCountriesFormat
                      @flightLeisureSearchSummaryForCurrentSelection.topCountries
                    }}
                  </span>
                </:value>
                <:label><div class="mt-100">{{t
                      "market_insight.top_searching_countries"
                    }}</div></:label>
              </PrismMetricBlock>
            </div>
          </div>
          {{#if this.hasFlightBusinessSearches}}
            <div class="divider-line"></div>
          {{/if}}
        {{/if}}

        {{#if this.hasFlightBusinessSearches}}
          <div class="mt-300 mb-300">
            <div class="search-type-title">
              <PrismIcon @name="plane" />
              {{t "market_insight.searches.flights.gds"}}
            </div>
            <div class="flex flex-row">
              <PrismMetricBlock
                @align="left"
                @compact={{true}}
                class="detail-pane-metric-block--search-level"
              >
                <:value>
                  <PrismBadge
                    @badgeType={{@flightBusinessSearchSummaryForCurrentSelection.numSearchesAsBadgeClass}}
                    @label={{@flightBusinessSearchSummaryForCurrentSelection.numSearchesAsLabel}}
                  />
                </:value>
                <:label>
                  <div class="mt-100">
                    {{t "market_insight.search_patterns.search_level"}}</div>
                </:label>
              </PrismMetricBlock>
              <PrismMetricBlock
                @align="left"
                @compact={{true}}
                class="detail-pane-metric-block--most-searched-los"
              >
                <:value>
                  <span
                    class="text-400--bold"
                  >{{@flightBusinessSearchSummaryForCurrentSelection.formattedMostSearchedLos}}</span>
                </:value>
                <:label>
                  <div class="mt-100">
                    {{t "market_insight.search_patterns.most_searched_los"}}
                  </div>
                </:label>
              </PrismMetricBlock>
              <PrismMetricBlock
                @align="left"
                @compact={{true}}
                class="detail-pane-metric-block--top-countries"
              >
                <:value>
                  <span class="text-400--bold">
                    {{topSearchingCountriesFormat
                      @flightBusinessSearchSummaryForCurrentSelection.topCountries
                    }}
                  </span>
                </:value>
                <:label>
                  <div class="mt-100">
                    {{t "market_insight.top_searching_countries"}}
                  </div>
                </:label>
              </PrismMetricBlock>
            </div>
          </div>
        {{/if}}

        {{#if this.hasHotelSearches}}
          <div class="divider-line"></div>
        {{/if}}

        {{#if this.hasHotelLeisureSearches}}
          <div class="mt-300 mb-300">
            <div class="search-type-title">
              <PrismIcon @name="business-type-hotel" />
              {{t "market_insight.searches.hotels"}}
              {{#if this.hasHotelBusinessSearches}}
                ({{t "general.meta"}}/{{t "general.ota"}})
              {{else if this.hasFlightBusinessSearches}}
                ({{t "general.meta"}}/{{t "general.ota"}}/{{t "general.GDS"}})
              {{/if}}
            </div>
            <div class="flex flex-row">
              <PrismMetricBlock
                @align="left"
                @compact={{true}}
                class="detail-pane-metric-block--search-level"
              >
                <:value>
                  <PrismBadge
                    @badgeType={{@hotelLeisureSearchSummaryForCurrentSelection.numSearchesAsBadgeClass}}
                    @label={{@hotelLeisureSearchSummaryForCurrentSelection.numSearchesAsLabel}}
                  />
                </:value>
                <:label><div class="mt-100">{{t
                      "market_insight.search_patterns.search_level"
                    }}</div></:label>
              </PrismMetricBlock>
              <PrismMetricBlock
                @align="left"
                @compact={{true}}
                class="detail-pane-metric-block--most-searched-los"
              >
                <:value>
                  <span
                    class="text-400--bold"
                  >{{@hotelLeisureSearchSummaryForCurrentSelection.formattedMostSearchedLos}}</span>
                </:value>
                <:label><div class="mt-100">{{t
                      "market_insight.search_patterns.most_searched_los"
                    }}</div></:label>
              </PrismMetricBlock>
              <PrismMetricBlock
                @align="left"
                @compact={{true}}
                class="detail-pane-metric-block--top-countries"
              >
                <:value>
                  <span class="text-400--bold">
                    {{topSearchingCountriesFormat
                      @hotelLeisureSearchSummaryForCurrentSelection.topCountries
                    }}
                  </span>
                </:value>
                <:label><div class="mt-100">{{t
                      "market_insight.top_searching_countries"
                    }}</div></:label>
              </PrismMetricBlock>
            </div>
          </div>
          {{#if this.hasFlightBusinessSearches}}
            <div class="divider-line"></div>
          {{/if}}
        {{/if}}

        {{#if this.hasHotelBusinessSearches}}
          <div class="mt-300 mb-100">
            <div class="search-type-title">
              <PrismIcon @name="business-type-hotel" />
              {{t "market_insight.searches.hotels.gds"}}
            </div>
            <div class="flex flex-row">
              <PrismMetricBlock
                @align="left"
                @compact={{true}}
                class="detail-pane-metric-block--search-level"
              >
                <:value>
                  <PrismBadge
                    @badgeType={{@hotelBusinessSearchSummaryForCurrentSelection.numSearchesAsBadgeClass}}
                    @label={{@hotelBusinessSearchSummaryForCurrentSelection.numSearchesAsLabel}}
                  />
                </:value>
                <:label><div class="mt-100">{{t
                      "market_insight.search_patterns.search_level"
                    }}</div></:label>
              </PrismMetricBlock>
              <PrismMetricBlock
                @align="left"
                @compact={{true}}
                class="detail-pane-metric-block--most-searched-los"
              >
                <:value>
                  <span
                    class="text-400--bold"
                  >{{@hotelBusinessSearchSummaryForCurrentSelection.formattedMostSearchedLos}}</span>
                </:value>
                <:label>
                  <div class="mt-100">
                    {{t "market_insight.search_patterns.most_searched_los"}}
                  </div>
                </:label>
              </PrismMetricBlock>
            </div>
          </div>
        {{/if}}
      </:content>
    </PrismCard>
  </template>
}
